import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  RefreshCw, Save, Eye, EyeOff, HelpCircle, Plus, Settings2, 
  ArrowLeftRight, ExternalLink, ShoppingBag
} from "lucide-react";

const API = "/api";

// HepsiBurada Marketplace Component
export function HepsiBuradaSettings({ token, credentials, onSave }) {
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [activeTab, setActiveTab] = useState("anasayfa");
  
  const [config, setConfig] = useState({
    // API Ayarları
    magazaId: credentials?.seller_id || "",
    servisAnahtari: credentials?.api_key || "",
    // Ürün Aktarım Ayarları
    urunAdi: "urun_adi",
    aciklama: "aciklama",
    markaBilgisi: "marka",
    satisFiyatTipi: "uye_tipi_fiyat_1",
    fiyatGonderimi: true,
    tlFiyataKomisyonEkle: false,
    komisyonTuru: "hicbiri",
    komisyonDegeri: 0,
    aktifPasifUrunleriAktar: false,
    urunBarkoduGonderilsin: true,
    dil: "tr",
    barkodOnEki: "",
    rezervStok: 0,
    varyantGrupId: "stok_kodu",
    urunKoduAlani: "stok_koduna_gore_aktar",
    marketplaceGrup: ""
  });

  useEffect(() => {
    if (credentials?.extra_config) {
      setConfig(prev => ({ ...prev, ...credentials.extra_config }));
    }
  }, [credentials]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        marketplace: "hepsiburada",
        seller_id: config.magazaId,
        api_key: config.servisAnahtari,
        extra_config: config
      });
      toast.success("HepsiBurada ayarları kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-lg border">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center text-white font-bold text-xl">
            HB
          </div>
          <div>
            <h2 className="text-2xl font-bold text-orange-600">HepsiBurada</h2>
            <p className="text-slate-500">Türkiye'nin en büyük e-ticaret platformu</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <ArrowLeftRight className="w-4 h-4 mr-2" />
            Pazaryeri Değiştir
          </Button>
          <Button variant="outline" size="sm">
            <HelpCircle className="w-4 h-4 mr-2" />
            Yardım
          </Button>
          <Button variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            YENİ EŞLEŞTİRME YAP
          </Button>
          <Button variant="outline" size="sm">
            <Settings2 className="w-4 h-4 mr-2" />
            OTOMATİK EŞLEŞTİRME
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start bg-slate-100 p-1">
          <TabsTrigger value="anasayfa" className="flex-1">Anasayfa</TabsTrigger>
          <TabsTrigger value="ayarlar" className="flex-1">Ayarlar</TabsTrigger>
          <TabsTrigger value="urun-aktarimi" className="flex-1">Ürün Aktarımı / Güncelleme</TabsTrigger>
          <TabsTrigger value="kampanyalarim" className="flex-1">Kampanyalarım</TabsTrigger>
        </TabsList>

        <TabsContent value="anasayfa" className="space-y-6 mt-6">
          {/* API Ayarları */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">API Ayarları</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Mağaza ID *</Label>
                  <Input 
                    value={config.magazaId}
                    onChange={(e) => setConfig({...config, magazaId: e.target.value})}
                    placeholder="b4ea9a5f-d980-4bdb-aeda-073d6d0a8e46"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Servis Anahtarı *</Label>
                  <div className="relative">
                    <Input 
                      type={showSecret ? "text" : "password"}
                      value={config.servisAnahtari}
                      onChange={(e) => setConfig({...config, servisAnahtari: e.target.value})}
                      placeholder="Servis anahtarınız"
                    />
                    <button 
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowSecret(!showSecret)}
                    >
                      {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
                {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                Kaydet
              </Button>
            </CardContent>
          </Card>

          {/* Ürün Aktarım Ayarları */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ürün Aktarım Ayarları</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {/* Kolon 1 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Ürün Adı</Label>
                    <Select value={config.urunAdi} onValueChange={(v) => setConfig({...config, urunAdi: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urun_adi">Ürün Adı</SelectItem>
                        <SelectItem value="urun_kodu">Ürün Kodu</SelectItem>
                        <SelectItem value="marka_urun_adi">Marka + Ürün Adı</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Satış Fiyat Tipi *</Label>
                    <Select value={config.satisFiyatTipi} onValueChange={(v) => setConfig({...config, satisFiyatTipi: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="uye_tipi_fiyat_1">Üye Tipi Fiyat 1</SelectItem>
                        <SelectItem value="uye_tipi_fiyat_2">Üye Tipi Fiyat 2</SelectItem>
                        <SelectItem value="satis_fiyati">Satış Fiyatı</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Komisyon Türü</Label>
                    <Select value={config.komisyonTuru} onValueChange={(v) => setConfig({...config, komisyonTuru: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hicbiri">Hiçbiri</SelectItem>
                        <SelectItem value="yuzde">Yüzde</SelectItem>
                        <SelectItem value="sabit">Sabit Tutar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg">
                    <Checkbox 
                      checked={config.urunBarkoduGonderilsin}
                      onCheckedChange={(c) => setConfig({...config, urunBarkoduGonderilsin: c})}
                    />
                    <Label className="text-orange-700">Ürün Barkodu Gönderilsin</Label>
                  </div>
                  <div className="space-y-2">
                    <Label>Rezerv Stok</Label>
                    <Input 
                      type="number"
                      value={config.rezervStok}
                      onChange={(e) => setConfig({...config, rezervStok: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Marketplace Grup</Label>
                    <Input 
                      value={config.marketplaceGrup}
                      onChange={(e) => setConfig({...config, marketplaceGrup: e.target.value})}
                    />
                  </div>
                </div>

                {/* Kolon 2 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Açıklama</Label>
                    <Select value={config.aciklama} onValueChange={(v) => setConfig({...config, aciklama: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aciklama">Açıklama</SelectItem>
                        <SelectItem value="kisa_aciklama">Kısa Açıklama</SelectItem>
                        <SelectItem value="detayli_aciklama">Detaylı Açıklama</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-orange-500 text-white rounded-lg">
                    <Checkbox 
                      checked={config.fiyatGonderimi}
                      onCheckedChange={(c) => setConfig({...config, fiyatGonderimi: c})}
                      className="border-white data-[state=checked]:bg-white data-[state=checked]:text-orange-500"
                    />
                    <Label className="text-white">Fiyat Gönderimi</Label>
                  </div>
                  <div className="space-y-2">
                    <Label>Komisyon Değeri</Label>
                    <Input 
                      type="number"
                      value={config.komisyonDegeri}
                      onChange={(e) => setConfig({...config, komisyonDegeri: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Dil *</Label>
                    <Select value={config.dil} onValueChange={(v) => setConfig({...config, dil: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tr">tr</SelectItem>
                        <SelectItem value="en">en</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Varyant Grup ID</Label>
                    <Select value={config.varyantGrupId} onValueChange={(v) => setConfig({...config, varyantGrupId: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="stok_kodu">Stok Kodu</SelectItem>
                        <SelectItem value="urun_kodu">Ürün Kodu</SelectItem>
                        <SelectItem value="barkod">Barkod</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Ürün Kodu Alanı</Label>
                    <Select value={config.urunKoduAlani} onValueChange={(v) => setConfig({...config, urunKoduAlani: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="stok_koduna_gore_aktar">Stok Koduna Göre Aktar</SelectItem>
                        <SelectItem value="barkoda_gore_aktar">Barkoda Göre Aktar</SelectItem>
                        <SelectItem value="urun_id">Ürün ID</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Kolon 3 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Marka Bilgisi</Label>
                    <Select value={config.markaBilgisi} onValueChange={(v) => setConfig({...config, markaBilgisi: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="marka">Marka</SelectItem>
                        <SelectItem value="uretici">Üretici</SelectItem>
                        <SelectItem value="tedarikci">Tedarikçi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.tlFiyataKomisyonEkle}
                      onCheckedChange={(c) => setConfig({...config, tlFiyataKomisyonEkle: c})}
                    />
                    <Label>TL Fiyata Komisyon Ekle</Label>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.aktifPasifUrunleriAktar}
                      onCheckedChange={(c) => setConfig({...config, aktifPasifUrunleriAktar: c})}
                    />
                    <Label>Aktif ve Pasif Ürünleri Aktar</Label>
                  </div>
                  <div className="space-y-2">
                    <Label>Barkod Ön Eki</Label>
                    <Input 
                      value={config.barkodOnEki}
                      onChange={(e) => setConfig({...config, barkodOnEki: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ayarlar">
          <Card>
            <CardContent className="pt-6">
              <p className="text-slate-500 text-center py-8">Genel ayarlar yakında eklenecek</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="urun-aktarimi">
          <Card>
            <CardContent className="pt-6">
              <p className="text-slate-500 text-center py-8">Ürün aktarım ve güncelleme özellikleri yakında eklenecek</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="kampanyalarim">
          <Card>
            <CardContent className="pt-6">
              <p className="text-slate-500 text-center py-8">Kampanya yönetimi yakında eklenecek</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// N11 Marketplace Component
export function N11Settings({ token, credentials, onSave }) {
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [activeTab, setActiveTab] = useState("ayarlar");

  const [config, setConfig] = useState({
    // API Ayarları
    apiAnahtari: credentials?.api_key || "",
    apiSifre: credentials?.api_secret || "",
    // Ürün Aktarım Ayarları
    urunAdi: "urun_adi",
    aciklama: "aciklama",
    markaBilgisi: "marka",
    urunAdinaKartIdEkle: true,
    aktifPasifUrunleriAktar: false,
    fiyatGonderimi: true,
    urunBarkoduGonderilsin: true,
    urunKoduAlani: "stok_koduna_gore_aktar",
    modelKodu: "stok_kodu",
    dil: "tr",
    tlFiyataKomisyonEkle: false,
    katalogIdIleHizliUrunEkleme: false,
    marketplaceGrup: "marketplace_grup_4",
    komisyonuUrunden_al: false,
    rezervStokuUrundenAl: false,
    rezervStok: 0,
    // Sipariş Aktarım Ayarları
    siparisDurumu: ["olusturuldu"]
  });

  useEffect(() => {
    if (credentials?.extra_config) {
      setConfig(prev => ({ ...prev, ...credentials.extra_config }));
    }
  }, [credentials]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        marketplace: "n11",
        api_key: config.apiAnahtari,
        api_secret: config.apiSifre,
        extra_config: config
      });
      toast.success("N11 ayarları kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-lg border">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-green-600 rounded-xl flex items-center justify-center text-white font-bold text-xl">
            n11
          </div>
          <div>
            <h2 className="text-2xl font-bold text-green-700">N11</h2>
            <p className="text-slate-500">Türkiye'nin önde gelen pazaryeri</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <ArrowLeftRight className="w-4 h-4 mr-2" />
            Pazaryeri Değiştir
          </Button>
          <Button variant="outline" size="sm">
            <HelpCircle className="w-4 h-4 mr-2" />
            Yardım
          </Button>
          <Button variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            YENİ EŞLEŞTİRME YAP
          </Button>
          <Button variant="outline" size="sm">
            <Settings2 className="w-4 h-4 mr-2" />
            OTOMATİK EŞLEŞTİRME
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start bg-slate-100 p-1">
          <TabsTrigger value="ayarlar" className="flex-1">Ayarlar</TabsTrigger>
          <TabsTrigger value="urun-aktarimi" className="flex-1">Ürün Aktarımı / Güncelleme</TabsTrigger>
        </TabsList>

        <TabsContent value="ayarlar" className="space-y-6 mt-6">
          {/* API Ayarları */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">API Ayarları</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>API Anahtarı *</Label>
                  <Input 
                    value={config.apiAnahtari}
                    onChange={(e) => setConfig({...config, apiAnahtari: e.target.value})}
                    placeholder="c25802f2-6353-4cce-9d81-b0fc6e3a5a98"
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Şifre *</Label>
                  <div className="relative">
                    <Input 
                      type={showSecret ? "text" : "password"}
                      value={config.apiSifre}
                      onChange={(e) => setConfig({...config, apiSifre: e.target.value})}
                      placeholder="API şifreniz"
                    />
                    <button 
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowSecret(!showSecret)}
                    >
                      {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
                {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                Kaydet
              </Button>
            </CardContent>
          </Card>

          {/* Ürün Aktarım Ayarları */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ürün Aktarım Ayarları</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {/* Kolon 1 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Ürün Adı *</Label>
                    <Select value={config.urunAdi} onValueChange={(v) => setConfig({...config, urunAdi: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="urun_adi">Ürün Adı</SelectItem>
                        <SelectItem value="urun_kodu">Ürün Kodu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-green-500 text-white rounded-lg">
                    <Checkbox 
                      checked={config.urunAdinaKartIdEkle}
                      onCheckedChange={(c) => setConfig({...config, urunAdinaKartIdEkle: c})}
                      className="border-white data-[state=checked]:bg-white data-[state=checked]:text-green-500"
                    />
                    <Label className="text-white">Ürün Adına Kart ID Ekle</Label>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-green-500 text-white rounded-lg">
                    <Checkbox 
                      checked={config.urunBarkoduGonderilsin}
                      onCheckedChange={(c) => setConfig({...config, urunBarkoduGonderilsin: c})}
                      className="border-white data-[state=checked]:bg-white data-[state=checked]:text-green-500"
                    />
                    <Label className="text-white">Ürün Barkodu Gönderilsin</Label>
                  </div>
                  <div className="space-y-2">
                    <Label>Model Kodu *</Label>
                    <Select value={config.modelKodu} onValueChange={(v) => setConfig({...config, modelKodu: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="stok_kodu">Stok Kodu</SelectItem>
                        <SelectItem value="barkod">Barkod</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Marketplace Grup</Label>
                    <Select value={config.marketplaceGrup} onValueChange={(v) => setConfig({...config, marketplaceGrup: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="marketplace_grup_1">Marketplace Grup 1</SelectItem>
                        <SelectItem value="marketplace_grup_2">Marketplace Grup 2</SelectItem>
                        <SelectItem value="marketplace_grup_3">Marketplace Grup 3</SelectItem>
                        <SelectItem value="marketplace_grup_4">Marketplace Grup 4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.komisyonuUrunden_al}
                      onCheckedChange={(c) => setConfig({...config, komisyonuUrunden_al: c})}
                    />
                    <Label>Komisyonu Üründen Al</Label>
                  </div>
                </div>

                {/* Kolon 2 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Açıklama *</Label>
                    <Select value={config.aciklama} onValueChange={(v) => setConfig({...config, aciklama: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aciklama">Açıklama</SelectItem>
                        <SelectItem value="kisa_aciklama">Kısa Açıklama</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.aktifPasifUrunleriAktar}
                      onCheckedChange={(c) => setConfig({...config, aktifPasifUrunleriAktar: c})}
                    />
                    <Label>Aktif ve Pasif Ürünleri Aktar</Label>
                  </div>
                  <div className="space-y-2">
                    <Label>Ürün Kodu Alanı *</Label>
                    <Select value={config.urunKoduAlani} onValueChange={(v) => setConfig({...config, urunKoduAlani: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="stok_koduna_gore_aktar">Stok Koduna Göre Aktar</SelectItem>
                        <SelectItem value="barkoda_gore_aktar">Barkoda Göre Aktar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Dil *</Label>
                    <Select value={config.dil} onValueChange={(v) => setConfig({...config, dil: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tr">tr</SelectItem>
                        <SelectItem value="en">en</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.rezervStokuUrundenAl}
                      onCheckedChange={(c) => setConfig({...config, rezervStokuUrundenAl: c})}
                    />
                    <Label>Rezerv Stoğu Üründen Al</Label>
                  </div>
                  <div className="space-y-2">
                    <Label>Rezerv Stok</Label>
                    <Input 
                      type="number"
                      value={config.rezervStok}
                      onChange={(e) => setConfig({...config, rezervStok: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>

                {/* Kolon 3 */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Marka Bilgisi *</Label>
                    <Select value={config.markaBilgisi} onValueChange={(v) => setConfig({...config, markaBilgisi: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="marka">Marka</SelectItem>
                        <SelectItem value="uretici">Üretici</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-green-500 text-white rounded-lg">
                    <Checkbox 
                      checked={config.fiyatGonderimi}
                      onCheckedChange={(c) => setConfig({...config, fiyatGonderimi: c})}
                      className="border-white data-[state=checked]:bg-white data-[state=checked]:text-green-500"
                    />
                    <Label className="text-white">Fiyat Gönderimi</Label>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.tlFiyataKomisyonEkle}
                      onCheckedChange={(c) => setConfig({...config, tlFiyataKomisyonEkle: c})}
                    />
                    <Label>TL Fiyata Komisyon Ekle</Label>
                  </div>
                  <div className="flex items-center gap-2 p-3 border rounded-lg">
                    <Checkbox 
                      checked={config.katalogIdIleHizliUrunEkleme}
                      onCheckedChange={(c) => setConfig({...config, katalogIdIleHizliUrunEkleme: c})}
                    />
                    <Label>Katalog ID ile Hızlı Ürün Ekleme</Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sipariş Aktarım Ayarları */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sipariş Aktarım Ayarları</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Sipariş Durumu *</Label>
                <div className="flex gap-2 flex-wrap">
                  <Badge className="bg-green-100 text-green-800">Oluşturuldu x</Badge>
                  <Badge variant="outline">+4</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="urun-aktarimi">
          <Card>
            <CardContent className="pt-6">
              <p className="text-slate-500 text-center py-8">Ürün aktarım ve güncelleme özellikleri yakında eklenecek</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default { HepsiBuradaSettings, N11Settings };
